/*
  NDIS 0138 Readiness — Source Verification helper (v0.2)
  ----------------------------------------------------------------------------
  Determines the report-level source verification status and builds the data
  consumed by `<SourceVerificationPanel />` on /results and /report, plus the
  "03 / SOURCE VERIFICATION" block in the plain-text and Markdown exports.

  Rules (conservative, intentional):
    - manualReview === true                       => DO_NOT_PUBLISH
    - pathway has no SIL / 0138 exposure          => INTERNAL_ONLY
    - all applicable OFFICIAL register sources are
      CURRENT and confirmedAgainstPublication    => VERIFIED
      (register-driven only; never speculative)
    - any other case                              => REQUIRES_SOURCE_VERIFICATION

  Status values returned here match the four controlled labels rendered in the
  Classifier Output card so the chip and the panel are always consistent.
  These are source-governance labels, not the six operational evidence states.
*/

import {
  SOURCES,
  AUTHORITY_LEVELS,
  SOURCE_STATUS,
  APPLIES_TO,
  getSourcesByApplication
} from "@/data/sourceRegister";

export const VERIFICATION_STATUS = {
  VERIFIED: "verified",
  REQUIRES_SOURCE_VERIFICATION: "requires_source_verification",
  INTERNAL_ONLY: "internal_only",
  DO_NOT_PUBLISH: "do_not_publish"
};

export const VERIFICATION_LABELS = {
  [VERIFICATION_STATUS.VERIFIED]: "VERIFIED",
  [VERIFICATION_STATUS.REQUIRES_SOURCE_VERIFICATION]: "REQUIRES SOURCE VERIFICATION",
  [VERIFICATION_STATUS.INTERNAL_ONLY]: "INTERNAL ONLY",
  [VERIFICATION_STATUS.DO_NOT_PUBLISH]: "DO NOT PUBLISH"
};

export const EXTENDED_BOUNDARY_STATEMENT =
  "NDIS 0138 Readiness is a consultancy readiness tool only. It is not legal advice, an Approved Quality Auditor report, an official NDIS Commission tool, a registration guarantee, or a compliance certification. Regulatory-sensitive findings should be checked against current NDIS Commission, NDIA, legal, audit, or clinical guidance where required.";

// Pathways that produce no outward-facing SIL / 0138 regulatory claim — the
// classifier output for these cases is internal categorisation only.
const INTERNAL_ONLY_PATHWAYS = new Set(["OUT_OF_SCOPE", "REGISTERED_NOT_SIL"]);

export function determineSourceVerificationStatus({ manualReview, pathwayId } = {}) {
  if (manualReview) return VERIFICATION_STATUS.DO_NOT_PUBLISH;
  if (pathwayId && INTERNAL_ONLY_PATHWAYS.has(pathwayId)) return VERIFICATION_STATUS.INTERNAL_ONLY;
  if (allApplicableOfficialSourcesConfirmed(pathwayId)) return VERIFICATION_STATUS.VERIFIED;
  return VERIFICATION_STATUS.REQUIRES_SOURCE_VERIFICATION;
}

// Register-driven VERIFIED check: true only when every OFFICIAL source that
// applies to this pathway is CURRENT and has been explicitly confirmed against
// its underlying publication in the source register.
//
// Advisory sources (`gateRole === "advisory"`) are preserved in the register for
// transparency but are excluded from this gate. Used to represent broader
// reform / in-progress official material that must not block SIL-specific
// verification.
function allApplicableOfficialSourcesConfirmed(pathwayId) {
  const officialSources = getSourcesByApplication(deriveAppliesTo(pathwayId)).filter(
    (s) => s.authorityLevel === AUTHORITY_LEVELS.OFFICIAL && s.gateRole !== "advisory"
  );
  if (officialSources.length === 0) return false;
  return officialSources.every(
    (s) => s.status === SOURCE_STATUS.CURRENT && s.confirmedAgainstPublication === true
  );
}

// Derive the applicability tags relevant to this case.
function deriveAppliesTo(pathwayId) {
  const tags = new Set([
    // Always rely on the founding source + internal blueprint.
    APPLIES_TO.PROGRAM_DEFINITION,
    APPLIES_TO.READINESS_FRAMEWORK,
    APPLIES_TO.OPERATING_LOGIC,
    APPLIES_TO.CLASSIFIER_RULES,
    // Always relevant: practice standards / evidence framework.
    APPLIES_TO.PRACTICE_STANDARDS,
    APPLIES_TO.EVIDENCE_REQUIREMENTS
  ]);
  switch (pathwayId) {
    case "SIL_EXITING":
      tags.add(APPLIES_TO.EXIT_TRANSITION);
      tags.add(APPLIES_TO.WIND_DOWN);
      tags.add(APPLIES_TO.SIL_TRANSITION);
      break;
    case "SIL_0138_IN_PROGRESS":
    case "SIL_0115_HOLDER_NEEDS_0138":
    case "UNREGISTERED_SIL":
      tags.add(APPLIES_TO.SIL_REGISTRATION);
      tags.add(APPLIES_TO.SIL_TRANSITION);
      tags.add(APPLIES_TO.ZERO_ONE_THREE_EIGHT_PATHWAY);
      tags.add(APPLIES_TO.ZERO_ONE_THREE_EIGHT_READINESS);
      tags.add(APPLIES_TO.SIL_CLAIMS);
      tags.add(APPLIES_TO.PAYMENT_CHANGES);
      tags.add(APPLIES_TO.JULY_2026_TRANSITION);
      break;
    case "REGISTERED_NOT_SIL":
    case "OUT_OF_SCOPE":
      // Internal categorisation only — no SIL-specific official source pull.
      break;
    default:
      // MANUAL_REVIEW or unknown — be conservative and pull all SIL/0138 sources.
      tags.add(APPLIES_TO.SIL_REGISTRATION);
      tags.add(APPLIES_TO.SIL_TRANSITION);
      tags.add(APPLIES_TO.ZERO_ONE_THREE_EIGHT_PATHWAY);
      tags.add(APPLIES_TO.JULY_2026_TRANSITION);
      break;
  }
  return Array.from(tags);
}

function describeSourceBasis(status, pathway) {
  switch (status) {
    case VERIFICATION_STATUS.DO_NOT_PUBLISH:
      return "A safeguarding or manual-review hold is in effect. The source basis behind this output has not been confirmed and the report must not be shared externally.";
    case VERIFICATION_STATUS.INTERNAL_ONLY:
      return "This report categorises the provider as outside the 0138 / SIL scope. The output relies on the consultancy's internal operating logic and is suitable for internal consultancy use only.";
    case VERIFICATION_STATUS.REQUIRES_SOURCE_VERIFICATION:
      return `Findings about the "${pathway?.title || "current"}" pathway depend on regulatory references that are self-reported and have not been independently confirmed. Each official source listed below should be opened and checked against its current published version before this report is relied on or published.`;
    case VERIFICATION_STATUS.VERIFIED:
      return "Every regulatory rule applied in this report is mapped to a current official source entry and has been confirmed against its underlying publication.";
    default:
      return "Source basis not determined.";
  }
}

function buildRequiresVerificationNotes(status, sources) {
  if (status === VERIFICATION_STATUS.DO_NOT_PUBLISH) {
    return [
      "Do not share this report externally under any circumstances until a qualified NDIS consultant has completed a guided manual review.",
      "Manual review reasons are listed elsewhere in the report \u2014 each one must be resolved before any source-verification claim can be made."
    ];
  }
  if (status === VERIFICATION_STATUS.INTERNAL_ONLY) {
    return [
      "Suitable for internal consultancy use only \u2014 not for client publication in its current form.",
      "If the provider's scope changes to include SIL / 0138 delivery, re-run the intake so the report can be source-verified against the relevant official guidance."
    ];
  }
  if (status === VERIFICATION_STATUS.REQUIRES_SOURCE_VERIFICATION) {
    const officialCount = sources.filter((s) => s.authorityLevel === AUTHORITY_LEVELS.OFFICIAL).length;
    return [
      `Confirm each of the ${officialCount} official source${officialCount === 1 ? "" : "s"} listed against its current published version before relying on or publishing this report.`,
      "Regulatory points are self-reported by the provider and have not been independently verified by this tool.",
      "Practice Standards references should be re-checked because the reform is in active transition."
    ];
  }
  // VERIFIED (register-driven): every applicable official source is CURRENT
  // and explicitly confirmed against its publication in the source register.
  return [
    "Every regulatory rule applied here has been mapped to a current official source entry and confirmed against its underlying publication."
  ];
}

export function buildSourceVerification({ classifier, result } = {}) {
  const manualReview = Boolean(result?.manualReview);
  const pathway = classifier?.pathway || null;
  const status = determineSourceVerificationStatus({
    manualReview,
    pathwayId: pathway?.id
  });

  const appliesTo = deriveAppliesTo(pathway?.id);
  const allSources = getSourcesByApplication(appliesTo);
  const officialSources = allSources.filter(
    (s) => s.authorityLevel === AUTHORITY_LEVELS.OFFICIAL
  );
  const internalSources = allSources.filter(
    (s) =>
      s.authorityLevel === AUTHORITY_LEVELS.FOUNDING_SOURCE ||
      s.authorityLevel === AUTHORITY_LEVELS.INTERNAL
  );

  const lastReviewedDate =
    allSources
      .map((s) => s.lastReviewedDate)
      .filter(Boolean)
      .sort()
      .reverse()[0] || null;

  return {
    status,
    statusLabel: VERIFICATION_LABELS[status],
    lastReviewedDate,
    sourceBasis: describeSourceBasis(status, pathway),
    officialSources,
    internalSources,
    requiresVerificationNotes: buildRequiresVerificationNotes(status, allSources),
    boundaryStatement: EXTENDED_BOUNDARY_STATEMENT
  };
}

// Re-export for callers that want to render raw status constants.
export { SOURCES, AUTHORITY_LEVELS, SOURCE_STATUS };
